import { motion } from "framer-motion";
import { EXPERIENCE, INCLUSIONS } from "@/lib/data";
import { fadeUp, inViewOnce } from "@/lib/motion";
import { Check } from "lucide-react";

export default function Experience() {
  return (
    <section
      data-testid="experience-section"
      className="relative py-24 md:py-36 border-t border-[color:var(--line)]"
    >
      <div className="mx-auto max-w-[1360px] px-5 md:px-8">
        <div className="flex items-center justify-between mb-14 md:mb-20">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[color:var(--ink-3)]">
            Ch. 03 — Experience
          </span>
          <span className="font-mono text-[11px] tabular-nums text-[color:var(--ink-3)]">
            §003 / 010
          </span>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={inViewOnce}
          transition={{ duration: 0.9 }}
          className="font-serif text-[38px] sm:text-[56px] md:text-[74px] leading-[0.98] tracking-[-0.03em] font-medium max-w-[960px] text-balance"
        >
          A live, guided{" "}
          <em className="italic font-normal text-[color:var(--copper-2)]">
            build experience.
          </em>
        </motion.h2>

        <motion.ol
          initial="hidden"
          whileInView="show"
          viewport={inViewOnce}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08 } },
          }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {EXPERIENCE.map((s, i) => (
            <motion.li
              key={s.title}
              variants={fadeUp}
              data-testid={`experience-step-${s.title.toLowerCase()}`}
              className="relative bg-[color:var(--bone-2)] border border-[color:var(--line-strong)] p-6 md:p-7 min-h-[180px] flex flex-col justify-between"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[color:var(--ink-3)]">
                  Step {s.n}
                </span>
                {i < EXPERIENCE.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="hidden lg:block absolute -right-2 top-1/2 -translate-y-1/2 text-[color:var(--copper)] font-mono text-[10px]"
                  >
                    →
                  </span>
                )}
              </div>
              <div className="mt-10">
                <h3 className="font-serif text-[28px] leading-none font-medium text-[color:var(--ink)]">
                  {s.title}
                </h3>
                <p className="mt-2 text-[14px] leading-[1.55] text-[color:var(--ink-2)]">
                  {s.body}
                </p>
              </div>
            </motion.li>
          ))}
        </motion.ol>

        {/* Inclusion strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={inViewOnce}
          transition={{ duration: 0.9 }}
          className="mt-14 md:mt-20 border border-[color:var(--line-strong)] bg-[color:var(--ink)] text-[color:var(--bone)]"
        >
          <div className="px-6 md:px-10 py-8 md:py-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-[color:var(--copper)]">
              Every certification pass includes
            </p>
            <ul className="mt-6 flex flex-wrap gap-x-8 gap-y-4">
              {INCLUSIONS.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2 text-[14px] font-medium"
                  data-testid={`inclusion-${item.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                >
                  <Check
                    size={14}
                    strokeWidth={2}
                    className="text-[color:var(--copper)]"
                  />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
